import { DmMessageAttachment } from '../DmMessageAttachment';
import { AttachmentTypeIcon } from './AttachmentTypeIcon';
import type { StagedAttachment, StagedFile } from './stagedAttachment';
import { useObjectUrl } from './useObjectUrl';

const UPLOAD_COMPLETE_PERCENT = 100;

interface PendingAttachmentPreviewProps {
    attachment: StagedAttachment;
    senderName: string;
}

function LocalFilePreview({ staged, senderName }: { staged: StagedFile; senderName: string }) {
    const url = useObjectUrl(staged.file);
    if (!url) return null;
    return (
        <DmMessageAttachment
            attachment={{ mimeType: staged.mimeType, url, previewUrl: null, previewMimeType: null }}
            senderName={senderName}
        />
    );
}

function PendingAttachmentPreview({ attachment, senderName }: PendingAttachmentPreviewProps) {
    if (attachment.source === 'file') {
        return <LocalFilePreview staged={attachment} senderName={senderName} />;
    }
    return (
        <span className="inline-flex items-center gap-2 max-w-full rounded-lg bg-black/10 px-2.5 py-1.5">
            <AttachmentTypeIcon mimeType={attachment.mimeType} className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium truncate">{attachment.title}</span>
        </span>
    );
}

function uploadLabel(attachment: StagedAttachment, uploadPercent: number | null): string {
    const uploading =
        attachment.source === 'file' &&
        uploadPercent !== null &&
        uploadPercent < UPLOAD_COMPLETE_PERCENT;
    return uploading ? `Uploading… ${uploadPercent}%` : 'Sending…';
}

function PendingUploadStatus({
    attachment,
    uploadPercent
}: {
    attachment: StagedAttachment;
    uploadPercent: number | null;
}) {
    return (
        <span role="status" className="flex items-center gap-1.5 text-xs opacity-90">
            <span className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
            {uploadLabel(attachment, uploadPercent)}
        </span>
    );
}

export { PendingAttachmentPreview, PendingUploadStatus };
